<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { computed, ref, watch } from "vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { formatDateTime } from "@/composables/formatUtils";
import { cnmcApi } from "./cnmcApi";
import type { CnmcContext, CnmcOrderFilings } from "./cnmcTypes";
import InfoCallout from "@/components/common/InfoCallout.vue";
import CnmcPlanProgress from "./CnmcPlanProgress.vue";
import { issuanceFileCount, planPeriod } from "./cnmcPresentation";
import CnmcIssuanceBatchDetail from "./CnmcIssuanceBatchDetail.vue";
const props = defineProps<{
  companyId: number;
  orderId: number;
  isAdmin?: boolean;
}>();
const data = ref<CnmcOrderFilings | null>(null);
const context = ref<CnmcContext | null>(null);
const showPlan = ref(false);
const canPrepare = computed(() => {
  const connection = context.value?.connections.find(
    (item) => item.id === data.value?.plan.registry_connection_id
  );
  return !!(
    context.value?.preparation_enabled &&
    connection?.active &&
    connection.preparation_enabled
  );
});
const { globalErrorHandler } = useGlobalHandlers();
const { locale } = useI18n();
let revision = 0;
const load = async () => {
  const current = ++revision;
  try {
    const [filings, settings] = await Promise.all([
      cnmcApi.order(props.companyId, props.orderId),
      cnmcApi.context(props.companyId),
    ]);
    if (current !== revision) return;
    data.value = filings.data;
    context.value = settings.data;
  } catch (error) {
    globalErrorHandler(error);
  }
};
watch(
  () => [props.companyId, props.orderId],
  () => {
    data.value = null;
    showPlan.value = false;
    void load();
  },
  { immediate: true }
);
</script>
<template>
  <section v-if="data" class="py-0 mb-3">
    <h5 class="eyebrow-label mb-3">{{ $t("cnmc.linked_filings") }}</h5>
    <InfoCallout icon="file-lines" class="mb-3">{{
      $t("cnmc.order_instructions")
    }}</InfoCallout>
    <div class="border rounded-3 p-3 mb-3">
      <div class="d-flex justify-content-between align-items-start gap-3 mb-3">
        <div>
          <strong class="small">{{ planPeriod(data.plan, locale) }}</strong>
          <div class="small text-body-secondary">
            {{ $t("cnmc.batch_number", { id: data.plan.id }) }}
          </div>
        </div>
        <button class="btn btn-link btn-sm p-0" @click="showPlan = true">
          {{ $t("cnmc.open_batch") }}
        </button>
      </div>
      <CnmcPlanProgress :plan="data.plan" />
      <div class="small text-body-secondary mt-2">
        {{
          $t(
            `cnmc.actions.${data.plan.next_action}`,
            issuanceFileCount(data.plan)
          )
        }}
      </div>
    </div>
    <ul v-if="data.expected.expected_plants">
      <li v-for="plant in data.expected.expected_plants" :key="plant.name">
        {{ plant.name }} · {{ plant.go_count }} GOs ·
        {{ plant.months.join(", ") }}
      </li>
    </ul>
    <div v-if="data.accepted" class="alert alert-success">
      {{ $t("cnmc.inventory_accepted") }} · {{ data.accepted.accepted_at }}
      <ul class="mb-0">
        <li
          v-for="certificate in data.accepted.certificates"
          :key="certificate.id"
        >
          #{{ certificate.id }} · {{ certificate.count }} GOs ·
          {{ certificate.reviewed_device_name || certificate.device_name }} ·
          {{ formatDateTime(certificate.imported_at) }}
        </li>
      </ul>
    </div>
    <CnmcIssuanceBatchDetail
      v-if="showPlan"
      :company-id="companyId"
      :can-edit="false"
      :can-review="context?.can_review"
      :isAdmin
      :plan="data.plan"
      :can-prepare="canPrepare"
      @close="showPlan = false"
      @update="
        data.plan = $event;
        load();
      "
    />
  </section>
</template>
